import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import {
  getMaterialsByFarmer, getSeasonsByFarmer,
  addMaterial, updateMaterialStatus, deleteMaterial,
} from '@/store';
import { EmptyState } from '@/components/base/EmptyState';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import { getFileDownloadUrl } from '@/api/file';
import { MaterialStats } from './components/MaterialStats';
import { MaterialFilters } from './components/MaterialFilters';
import { MaterialCard } from './components/MaterialCard';
import { MaterialFormModal } from './components/MaterialFormModal';
import type { DiaryAttachment, Material } from '@/types';

export default function FarmerMaterialPage() {
  useStore();
  const { user } = useAuth();
  const farmerId = user?.farmerId || 'f1';

  const allMaterials = getMaterialsByFarmer(farmerId);
  const seasons = getSeasonsByFarmer(farmerId);

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Material | null>(null);
  const [viewItem, setViewItem] = useState<Material | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!viewItem?.attachments?.length) return;
    let cancelled = false;
    viewItem.attachments.forEach((a) => {
      if (attachmentUrls[a.fileId]) return;
      getFileDownloadUrl(a.fileId)
        .then((url) => {
          if (!cancelled) setAttachmentUrls((prev) => ({ ...prev, [a.fileId]: url }));
        })
        .catch(() => {
          // Thumbnail preview only — leave it unresolved on failure.
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewItem]);

  const [search, setSearch] = useState('');
  const [filterNhom, setFilterNhom] = useState('');
  const [filterNguon, setFilterNguon] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeasonId, setFilterSeasonId] = useState('');

  const nhoms = useMemo(() => {
    const set = new Set<string>();
    allMaterials.forEach((m) => { if (m.nhomVatTu) set.add(m.nhomVatTu); });
    return Array.from(set).sort();
  }, [allMaterials]);

  const filteredMaterials = useMemo(() => {
    let list = [...allMaterials];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((m) =>
        m.tenVatTu.toLowerCase().includes(q) ||
        (m.maVatTu || '').toLowerCase().includes(q)
      );
    }
    if (filterNhom) list = list.filter((m) => m.nhomVatTu === filterNhom);
    if (filterNguon) list = list.filter((m) => m.nguonGoc === filterNguon);
    if (filterStatus) list = list.filter((m) => m.status === filterStatus);
    if (filterSeasonId) list = list.filter((m) => m.seasonId === filterSeasonId);
    return list.sort((a, b) => (b.ngayCap > a.ngayCap ? 1 : -1));
  }, [allMaterials, search, filterNhom, filterNguon, filterStatus, filterSeasonId]);

  const tong = allMaterials.length;
  const htxCapUng = allMaterials.filter((m) => m.nguonGoc === 'HTX cấp/ứng').length;
  const tuMua = allMaterials.filter((m) => m.nguonGoc === 'Nông hộ tự mua').length;
  const sapHet = allMaterials.filter((m) => m.status === 'Sắp hết').length;
  const thieu = allMaterials.filter((m) => m.status === 'Thiếu').length;

  const openCreate = () => { setEditItem(null); setFormOpen(true); };
  const openEdit = (item: Material) => { setEditItem(item); setFormOpen(true); };

  const handleSave = async (data: {
    tenVatTu: string; nhomVatTu: string; nguonGoc: Material['nguonGoc'];
    soLuong: number; tonKho: number; donVi: string; ngayCap: string;
    seasonId: string; ghiChu?: string; status: Material['status'];
    attachments?: DiaryAttachment[];
  }) => {
    try {
      if (editItem) {
        // The real backend only supports a status transition on an existing
        // Material — no full edit route exists (see store/materials.ts).
        await updateMaterialStatus(editItem.id, data.status, farmerId);
        showToast('Cập nhật trạng thái vật tư thành công', 'success');
      } else {
        await addMaterial({
          farmerId,
          tenVatTu: data.tenVatTu, nhomVatTu: data.nhomVatTu, nguonGoc: 'Nông hộ tự mua',
          soLuong: data.soLuong, tonKho: data.tonKho, donVi: data.donVi,
          ngayCap: data.ngayCap, seasonId: data.seasonId || undefined,
          ghiChu: data.ghiChu, status: data.status, attachments: data.attachments,
        });
        showToast('Thêm vật tư thành công', 'success');
      }
      setFormOpen(false);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lưu vật tư thất bại', 'error');
    }
  };

  const handleDelete = async (item: Material) => {
    const ok = await confirmModal({
      title: 'Xóa vật tư',
      message: `Xóa "${item.tenVatTu}" (${item.tonKho ?? item.soLuong} ${item.donVi}) khỏi danh sách vật tư? Thao tác này không thể hoàn tác.`,
      confirmText: 'Xóa',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMaterial(item.id, farmerId);
      // The detail sheet may be showing the row that just disappeared.
      setViewItem((v) => (v?.id === item.id ? null : v));
      showToast('Đã xóa vật tư', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Xóa vật tư thất bại', 'error');
    }
  };

  return (
    <div className="pb-4 max-w-[800px] mx-auto">
      <div className="mx-4 mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Vật tư</h2>
            <p className="text-[10px] text-slate-400 mt-0.5">Theo dõi phân bón, hóa chất, vật tư nông nghiệp</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-2 rounded-xl text-xs font-bold bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[44px] flex items-center gap-1"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></span>
            Thêm
          </button>
        </div>

        <MaterialStats tongVatTu={tong} htxCapUng={htxCapUng} tuMua={tuMua} sapHet={sapHet} thieu={thieu} />

        <MaterialFilters
          search={search} onSearchChange={setSearch}
          nhom={filterNhom} onNhomChange={setFilterNhom}
          nguon={filterNguon} onNguonChange={setFilterNguon}
          status={filterStatus} onStatusChange={setFilterStatus}
          seasonId={filterSeasonId} onSeasonChange={setFilterSeasonId}
          nhoms={nhoms} seasons={seasons.map((s) => ({ id: s.id, name: s.name }))}
        />

        {filteredMaterials.length === 0 ? (
          <EmptyState
            icon="ri-seedling-line"
            title="Chưa có vật tư"
            description="Hãy thêm vật tư đầu tiên hoặc chờ HTX cấp."
          />
        ) : (
          <div className="space-y-2">
            {filteredMaterials.map((m) => (
              <MaterialCard
                key={m.id}
                material={m}
                season={seasons.find((s) => s.id === m.seasonId)}
                onView={() => setViewItem(m)}
                onEdit={() => openEdit(m)}
                onDelete={() => handleDelete(m)}
              />
            ))}
          </div>
        )}
      </div>

      {formOpen && (
        <MaterialFormModal
          editItem={editItem}
          seasons={seasons}
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
      )}

      {viewItem && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/50 animate-modal-backdrop" onClick={() => setViewItem(null)} />
          <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-sm font-semibold text-slate-900">Chi tiết vật tư</h3>
              <button type="button" onClick={() => setViewItem(null)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-2 py-0.5">{viewItem.maVatTu || viewItem.id}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  viewItem.nguonGoc === 'HTX cấp/ứng' ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-600'
                }`}>{viewItem.nguonGoc || '-'}</span>
              </div>
              <p className="text-sm font-bold text-slate-900">{viewItem.tenVatTu}</p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-slate-400">Nhóm</p><p className="font-medium">{viewItem.nhomVatTu || '-'}</p></div>
                <div><p className="text-slate-400">Trạng thái</p><p className="font-medium">{viewItem.status}</p></div>
                <div><p className="text-slate-400">Tồn kho</p><p className="font-medium">{viewItem.tonKho ?? viewItem.soLuong} {viewItem.donVi}</p></div>
                <div><p className="text-slate-400">Số lượng nhập</p><p>{viewItem.soLuong} {viewItem.donVi}</p></div>
                <div><p className="text-slate-400">Ngày nhập</p><p>{viewItem.ngayCap}</p></div>
                {viewItem.seasonId && (
                  <div><p className="text-slate-400">Mùa vụ</p><p>{seasons.find((s) => s.id === viewItem.seasonId)?.name || '-'}</p></div>
                )}
              </div>
              {viewItem.ghiChu && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-[10px] text-slate-400">Ghi chú</p>
                  <p className="text-xs text-slate-700">{viewItem.ghiChu}</p>
                </div>
              )}
              {viewItem.attachments && viewItem.attachments.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 mb-1.5">Ảnh / chứng từ</p>
                  <div className="grid grid-cols-3 gap-2">
                    {viewItem.attachments.map((a) => {
                      const url = attachmentUrls[a.fileId];
                      return (
                        <div key={a.fileId} className="aspect-square rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center">
                          {!url && <span className="text-[10px] text-slate-400">Đang tải...</span>}
                          {url && a.type === 'video' && <video src={url} controls className="w-full h-full object-cover" />}
                          {url && a.type === 'image' && <img src={url} alt={a.caption ?? 'ảnh'} className="w-full h-full object-cover" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3">
              <button type="button" onClick={() => setViewItem(null)} className="w-full px-4 py-2.5 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}